'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ALERT_TYPE_ORDER } from '@alltrails/alerts/constants';
import { alertTypeIcon, alertTypeIconColor } from '@alltrails/alerts/utils/alertTypeDisplay';
import { alertTypeLabel } from '@alltrails/alerts/utils/alertTypeLabel';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import Typography from '@alltrails/core/components/Typography';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSmall from '@alltrails/denali/icons/ChevronSm';
import type { Group } from '@alltrails/groups/types';
import { AlertStatus, AlertType } from '@alltrails/types/Alert';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type TrailInfoSummary from '@alltrails/types/TrailInfoSummary';
import { MOBILE_TOP_ALERT_TYPES } from '../../utils/constants';
import styles from './AlertsButton.module.scss';

const AlertsModal = dynamic(() => import('@alltrails/alerts/components/AlertsModal/AlertsModal'), { ssr: false });

const alertTypeClass: Partial<Record<AlertType, string>> = {
  [AlertType.WARNING]: styles.caution,
  [AlertType.INFORMATION]: styles.info
};

type AlertsButtonProps = {
  isAdmin?: boolean;
  isPLPUser?: boolean;
  trailDetails: TrailDetails;
  trailInfo?: TrailInfoSummary;
  userGroups?: Group[];
};

const AlertsButton = ({ isAdmin, isPLPUser, trailDetails, trailInfo, userGroups }: AlertsButtonProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);

  const visibleAlerts = trailDetails.alerts?.filter(a => a.status === AlertStatus.ACTIVE || (isAdmin && a.status === AlertStatus.SCHEDULED)) ?? [];
  const hasTopAlerts = visibleAlerts.some(a => MOBILE_TOP_ALERT_TYPES.has(a.alertType));
  const lowerAlerts = visibleAlerts.filter(a => !MOBILE_TOP_ALERT_TYPES.has(a.alertType));

  if (!isMobile || hasTopAlerts || lowerAlerts.length === 0) {
    return null;
  }

  const primaryType = ALERT_TYPE_ORDER.find(type => lowerAlerts.some(a => a.alertType === type));

  if (!primaryType) {
    return null;
  }

  const IconComponent = alertTypeIcon[primaryType];
  const iconColor = alertTypeIconColor[primaryType];
  const typeClass = alertTypeClass[primaryType] ?? styles.caution;

  const onAlertsButtonClick = () => {
    setIsAlertsModalOpen(true);
    logTrailDetailsOverviewLinkClicked({ overview_link_clicked: 'alert', trail_id: trailDetails.id });
  };

  return (
    <>
      <div className={classNames(styles.tableRow, typeClass)}>
        <button className={styles.mobileAlertsButton} onClick={onAlertsButtonClick} type="button">
          <div className={styles.icon}>
            <IconComponent color={iconColor} />
          </div>
          <div className={styles.text}>
            <Typography className={styles.link} component="div" variant="text200">
              {alertTypeLabel(primaryType, intl)}
            </Typography>
            {visibleAlerts.length > 1 && (
              <div className={styles.sub}>
                <FormattedMessage defaultMessage="{count, plural, one {# alert} other {# alerts}}" values={{ count: visibleAlerts.length }} />
              </div>
            )}
          </div>
          <ChevronSmall className={styles.chevron} orientation="right" />
        </button>
      </div>
      {isAlertsModalOpen && (
        <AlertsModal
          isAdmin={isAdmin}
          isPLPUser={isPLPUser}
          onClose={() => setIsAlertsModalOpen(false)}
          trailDetails={trailDetails}
          trailInfo={trailInfo}
          userGroups={userGroups}
        />
      )}
    </>
  );
};

export default AlertsButton;
